// Sermon notes: shaping the AI's answer, and the text / printable handout made from it.

const asText = (v) => (typeof v === 'string' ? v.trim() : '');
const asList = (v) => (Array.isArray(v) ? v.map(asText).filter(Boolean) : []);

/** Whatever the AI returned -> a summary with every field the app expects. */
export function normalizeSummary(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    theme: asText(r.theme),
    summary: asText(r.summary),
    keyPoints: asList(r.keyPoints),
    verses: asList(r.verses),
    discussionQuestions: asList(r.discussionQuestions),
    takeaway: asText(r.takeaway),
  };
}

const headerLines = ({ title, speaker }, date) => {
  const lines = [];
  if (title) lines.push(title);
  if (speaker) lines.push(`Speaker: ${speaker}`);
  lines.push(`Date: ${date}`);
  return lines;
};

/** The plain-text version (download and copy). */
export function formatSermonSummary(sermonSummary, date, details = {}) {
  let text = `SERMON SUMMARY - ${date}\n${'='.repeat(40)}\n`;
  if (details.title || details.speaker) text += `${headerLines(details, date).join('\n')}\n`;
  text += '\n';

  if (sermonSummary.theme) {
    text += `THEME\n${sermonSummary.theme}\n\n`;
  }
  if (sermonSummary.summary) {
    text += `SUMMARY\n${sermonSummary.summary}\n\n`;
  }
  if (sermonSummary.keyPoints && sermonSummary.keyPoints.length > 0) {
    text += `KEY POINTS\n`;
    sermonSummary.keyPoints.forEach((point, i) => {
      text += `  ${i + 1}. ${point}\n`;
    });
    text += '\n';
  }
  if (sermonSummary.verses && sermonSummary.verses.length > 0) {
    text += `REFERENCED VERSES\n${sermonSummary.verses.join(', ')}\n\n`;
  }
  if (sermonSummary.discussionQuestions && sermonSummary.discussionQuestions.length > 0) {
    text += `DISCUSSION QUESTIONS\n`;
    sermonSummary.discussionQuestions.forEach((q, i) => {
      text += `  ${i + 1}. ${q}\n`;
    });
    text += '\n';
  }
  if (sermonSummary.takeaway) {
    text += `THIS WEEK\n${sermonSummary.takeaway}\n`;
  }
  return text.replace(/\n+$/, '\n');
}

const esc = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

/**
 * A small-group handout as a complete HTML page, ready to print or save as PDF.
 * Every value is escaped, so nothing the AI or the operator typed can run as markup.
 */
export function buildHandoutHtml(sermonSummary, date, details = {}) {
  const section = (heading, body) => (body ? `<h2>${esc(heading)}</h2>${body}` : '');
  const list = (tag, items) => (items && items.length ? `<${tag}>${items.map(i => `<li>${esc(i)}</li>`).join('')}</${tag}>` : '');
  const title = details.title || 'Sermon Notes';
  const meta = [details.speaker ? `Speaker: ${details.speaker}` : '', date].filter(Boolean).join(' · ');

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  body{font-family:Georgia,'Times New Roman',serif;color:#111;max-width:680px;margin:40px auto;padding:0 24px;line-height:1.55}
  h1{font-size:28px;margin:0 0 4px}
  .meta{color:#555;margin-bottom:24px}
  .theme{font-size:18px;font-style:italic;margin-bottom:8px}
  h2{font-size:15px;letter-spacing:.08em;text-transform:uppercase;color:#1e3a8a;border-bottom:1px solid #cbd5e1;padding-bottom:4px;margin:26px 0 8px}
  li{margin-bottom:6px}
  .lines li{list-style:none;border-bottom:1px solid #94a3b8;height:30px}
  .lines{padding:0}
  @media print{body{margin:0}}
</style></head><body>
<h1>${esc(title)}</h1>
<div class="meta">${esc(meta)}</div>
${sermonSummary.theme ? `<div class="theme">${esc(sermonSummary.theme)}</div>` : ''}
${section('Summary', sermonSummary.summary ? `<p>${esc(sermonSummary.summary)}</p>` : '')}
${section('Key points', list('ol', sermonSummary.keyPoints))}
${section('Scriptures', list('ul', sermonSummary.verses))}
${section('Discussion questions', list('ol', sermonSummary.discussionQuestions))}
${section('This week', sermonSummary.takeaway ? `<p>${esc(sermonSummary.takeaway)}</p>` : '')}
${section('My notes', '<ul class="lines"><li></li><li></li><li></li><li></li><li></li></ul>')}
</body></html>`;
}
